import { useCallback, useEffect, useState } from 'react'
import { navigateTo } from '../../app/routes'
import apiClient from '../../services/apiClient'
import { clearSession, getToken } from '../../services/authStorage'

export function useAdminDashboardApi() {
  const [apiToken, setApiToken] = useState(null)
  const [adminData, setAdminData] = useState(null)
  const [apiStatus, setApiStatus] = useState('Connecting to OBC API...')
  const isAdminLoading = !adminData && apiStatus.startsWith('Connecting')

  useEffect(() => {
    let isMounted = true

    async function loadAdmin() {
      const token = getToken()

      if (!token) {
        navigateTo('/login')
        return
      }

      try {
        const overview = await apiClient.adminOverview(token)

        if (isMounted) {
          setApiToken(token)
          setAdminData(overview)
          setApiStatus('Live backend connected')
        }
      } catch (error) {
        clearSession()
        if (isMounted) {
          setApiStatus(error.message || 'Session expired')
          navigateTo('/login')
        }
      }
    }

    loadAdmin()

    return () => {
      isMounted = false
    }
  }, [])

  const refreshAdmin = useCallback(async (message = 'Admin data refreshed') => {
    if (!apiToken) return
    try {
      const overview = await apiClient.adminOverview(apiToken)
      setAdminData(overview)
      setApiStatus(message)
    } catch (error) {
      setApiStatus(error.message || 'Admin refresh failed')
    }
  }, [apiToken])

  const downloadAdminCsv = useCallback(async (token = apiToken, report, filename, onRefresh) => {
    if (!token) return

    try {
      const blob = await apiClient.downloadAdminExport(token, report)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
      await onRefresh?.(`${filename} exported`)
    } catch (error) {
      await onRefresh?.(error.message || 'Export failed')
    }
  }, [apiToken])

  const clearAdminStatus = useCallback(() => {
    setApiStatus('')
  }, [])

  return {
    adminData,
    apiStatus,
    apiToken,
    clearAdminStatus,
    downloadAdminCsv,
    isAdminLoading,
    refreshAdmin,
  }
}
